/**
* Shopify Product Image Watermark Protection v4.7
* 
* 配置说明（修改 CONFIG 对象）：
* - showTextOnPreview: true/false — 预览窗口是否显示说明文字（默认 false）
* - watermarkColorMode: 'auto'/'white'/'black'/'lightgrey' — 水印颜色（默认 lightgrey）
* - opacity: 0-1 — 水印透明度（默认 0.35）
*/

(function() {
 'use strict';

 const CONFIG = {
   // ===== 基本配置 =====
   watermarkText: '© ' + (window.Shopify?.shop?.name || window.location.hostname.replace(/^www\./, '')),
   
   // ===== 水印样式配置 =====
   opacity: 0.35,
   rotateAngle: -30,
   spacing: 250,
   
   // ===== 预览窗口文字显示配置 =====
   // 设为 true 显示说明文字，false 只显示图片和按钮（默认 false）
   showTextOnPreview: false,
   
   // ===== 水印颜色模式 =====
   // 'lightgrey' = 始终浅灰色（推荐，效果最稳定）
   // 'auto' = 自动检测图片亮度调整颜色
   // 'white' = 始终白色
   // 'black' = 始终黑色
   watermarkColorMode: 'auto',
   
   // ===== 隐形水印 =====
   invisibleWatermark: true,
   invisibleText: 'CONFIDENTIAL',
   invisibleOpacity: 0.04,
   
   debug: false
 };

 const log = CONFIG.debug ? console.log.bind(console, '[Watermark]') : () => {};

 function createCanvas(width, height) {
   const canvas = document.createElement('canvas');
   canvas.width = width;
   canvas.height = height;
   return canvas;
 }

 function getHighResUrl(src) {
   if (!src) return '';
   
   let url = src;
   if (url.startsWith('//')) {
     url = 'https:' + url;
   }
   
   url = url.split('?')[0];
   
   url = url
     .replace(/_\d+x\d+\./g, '.')
     .replace(/_\d+x\./g, '.')
     .replace(/_small\./g, '.')
     .replace(/_medium\./g, '.')
     .replace(/_large\./g, '.')
     .replace(/_grande\./g, '.')
     .replace(/_compact\./g, '.')
     .replace(/_thumb\./g, '.')
     .replace(/_icon\./g, '.')
     .replace(/_pico\./g, '.')
     .replace(/_master\./g, '.')
     .replace(/_1024x1024\./g, '.')
     .replace(/_2048x2048\./g, '.')
     .replace(/_crop_[a-zA-Z]+/g, '');
   
   url = url.replace(/\.\.+/g, '.');
   
   return url;
 }

 // ============ 从 srcset 获取最大图 ============

 function getLargestImageFromSrcset(imgElement) {
   const srcset = imgElement.getAttribute('srcset') 
               || imgElement.getAttribute('data-srcset')
               || imgElement.getAttribute('data-lazy-srcset');
   
   if (srcset) {
     const sources = srcset.split(',').map(s => s.trim());
     
     let maxWidth = 0;
     let maxUrl = '';
     
     for (const source of sources) {
       const parts = source.split(/\s+/);
       const url = parts[0];
       const widthStr = parts[1] || '';
       const widthMatch = widthStr.match(/(\d+)w/);
       const width = widthMatch ? parseInt(widthMatch[1], 10) : 0;
       
       if (width > maxWidth) {
         maxWidth = width;
         maxUrl = url;
       }
     }
     
     if (maxUrl) {
       log('Using srcset max image:', maxWidth + 'w');
       return maxUrl;
     }
   }
   
   const dataSrc = imgElement.getAttribute('data-src') 
                || imgElement.getAttribute('data-lazy-src')
                || imgElement.getAttribute('data-original');
   if (dataSrc) {
     log('Using data-src');
     return dataSrc;
   }
   
   log('Using src');
   return imgElement.src;
 }

 // ============ 检测图片亮度，返回水印颜色 ============

 function getWatermarkColor(ctx, width, height) {
   if (CONFIG.watermarkColorMode === 'lightgrey') {
     return `rgba(180, 180, 180, 0.35)`;
   }
   if (CONFIG.watermarkColorMode === 'white') {
     return `rgba(255, 255, 255, ${CONFIG.opacity})`;
   }
   if (CONFIG.watermarkColorMode === 'black') {
     return `rgba(0, 0, 0, ${CONFIG.opacity})`;
   }
   
   // auto 模式
   try {
     const sampleSize = Math.min(100, Math.floor(width / 4));
     const sampleX = Math.floor((width - sampleSize) / 2);
     const sampleY = Math.floor((height - sampleSize) / 2);
     
     const imageData = ctx.getImageData(sampleX, sampleY, sampleSize, sampleSize);
     const data = imageData.data;
     
     let totalBrightness = 0;
     let pixelCount = 0;
     
     for (let i = 0; i < data.length; i += 40) {
       const r = data[i];
       const g = data[i + 1];
       const b = data[i + 2];
       const brightness = (r * 0.299 + g * 0.587 + b * 0.114);
       totalBrightness += brightness;
       pixelCount++;
     }
     
     const avgBrightness = totalBrightness / pixelCount;
     log('Image average brightness:', avgBrightness);
     
     if (avgBrightness > 180) {
       return `rgba(160, 160, 160, 0.35)`;
     } else if (avgBrightness > 128) {
       return `rgba(200, 200, 200, 0.35)`;
     } else {
       return `rgba(255, 255, 255, ${CONFIG.opacity})`;
     }
   } catch (err) {
     return `rgba(180, 180, 180, 0.35)`;
   }
 }

 // ============ Canvas 水印生成 ============

 async function generateCanvasWatermark(originalSrc) {
   const cleanSrc = getHighResUrl(originalSrc);
   
   const attemptUrls = [
     cleanSrc + '?_wm=' + Date.now(),
     originalSrc,
   ];

   for (const url of [...new Set(attemptUrls)]) {
     try {
       return await tryCanvasLoad(url);
     } catch (err) {
       log('Canvas attempt failed:', err.message);
     }
   }
   throw new Error('All Canvas attempts failed');
 }

 function tryCanvasLoad(url) {
   return new Promise((resolve, reject) => {
     const img = new Image();
     img.crossOrigin = 'anonymous';
     img.referrerPolicy = 'no-referrer';
     
     let timeout = setTimeout(() => reject(new Error('Timeout')), 10000);
     
     img.onload = function() {
       clearTimeout(timeout);
       try {
         const w = img.naturalWidth || img.width || 800;
         const h = img.naturalHeight || img.height || 600;
         
         log('Image loaded:', w, 'x', h);
         
         const canvas = createCanvas(w, h);
         const ctx = canvas.getContext('2d');
         
         ctx.drawImage(img, 0, 0);
         canvas.toDataURL('image/jpeg', 0.1);
         
         const watermarkColor = getWatermarkColor(ctx, w, h);
         log('Watermark color:', watermarkColor);
         
         drawVisibleWatermark(ctx, w, h, CONFIG.watermarkText, watermarkColor);
         if (CONFIG.invisibleWatermark) {
           drawInvisibleWatermark(ctx, w, h);
         }
         
         resolve(canvas.toDataURL('image/jpeg', 0.92));
       } catch (err) {
         reject(err);
       }
     };
     
     img.onerror = () => {
       clearTimeout(timeout);
       reject(new Error('Load failed'));
     };
     
     img.src = url;
   });
 }

 function drawVisibleWatermark(ctx, width, height, text, color) {
   const fontSize = Math.max(12, Math.min(Math.floor(Math.min(width, height) / 6), 56));
   const spacing = Math.max(60, Math.floor(Math.min(width, height) / 2.5));
   
   ctx.font = `bold ${fontSize}px Arial, sans-serif`;
   ctx.fillStyle = color || `rgba(180, 180, 180, 0.35)`;
   ctx.textAlign = 'center';
   ctx.textBaseline = 'middle';
   
   ctx.save();
   ctx.translate(width / 2, height / 2);
   ctx.rotate((CONFIG.rotateAngle * Math.PI) / 180);
   
   const cols = Math.ceil(width / spacing) + 4;
   const rows = Math.ceil(height / spacing) + 4;
   const startX = -cols * spacing / 2;
   const startY = -rows * spacing / 2;
   
   for (let i = 0; i < cols; i++) {
     for (let j = 0; j < rows; j++) {
       ctx.fillText(text, startX + i * spacing, startY + j * spacing);
     }
   }
   ctx.restore();
 }

 function drawInvisibleWatermark(ctx, width, height) {
   const fontSize = Math.max(8, Math.floor(Math.min(width, height) / 40));
   ctx.font = `${fontSize}px Arial, sans-serif`;
   ctx.fillStyle = `rgba(200, 200, 200, ${CONFIG.invisibleOpacity})`;
   ctx.textAlign = 'center';
   ctx.textBaseline = 'middle';
   
   const spacing = Math.max(30, Math.floor(Math.min(width, height) / 15));
   const cols = Math.ceil(width / spacing) + 2;
   const rows = Math.ceil(height / spacing) + 2;
   
   for (let i = -1; i < cols; i++) {
     for (let j = -1; j < rows; j++) {
       const x = i * spacing + (j % 2) * (spacing / 2);
       const y = j * spacing;
       ctx.save();
       ctx.translate(x, y);
       ctx.rotate((45 * Math.PI) / 180);
       ctx.fillText(CONFIG.invisibleText, 0, 0);
       ctx.restore();
     }
   }
 }

 // ============ 统一预览层 ============

 function showPreview(dataUrl, isCanvas) {
   const existing = document.getElementById('wm-preview-layer');
   if (existing) existing.remove();
   
   const layer = document.createElement('div');
   layer.id = 'wm-preview-layer';
   layer.style.cssText = `
     position: fixed;
     top: 0;
     left: 0;
     width: 100%;
     height: 100%;
     background: rgba(0,0,0,0.92);
     z-index: 999999;
     display: flex;
     flex-direction: column;
     align-items: center;
     justify-content: center;
     padding: 20px;
     overflow: auto;
   `;
   
   const encodedText = CONFIG.watermarkText.replace(/"/g, '&quot;');
   
   const textSection = CONFIG.showTextOnPreview ? `
     <div style="margin-top:24px;padding:18px 32px;background:rgba(255,255,255,0.1);border-radius:12px;text-align:center;max-width:600px;">
       <h3 style="color:#fff;font-size:16px;margin-bottom:8px;">🔒 Protected by <span style="color:#60a5fa;">${encodedText}</span></h3>
       <p style="color:rgba(255,255,255,0.6);font-size:13px;margin-bottom:12px;">This image has been watermarked.<br>Right-click and select "Save image as", or use the button below.</p>
       <button id="wm-download-btn" style="padding:12px 28px;background:#3b82f6;color:white;border:none;border-radius:8px;font-size:14px;cursor:pointer;">💾 Save Image</button>
     </div>
   ` : `
     <div style="margin-top:16px;">
       <button id="wm-download-btn" style="padding:12px 28px;background:#3b82f6;color:white;border:none;border-radius:8px;font-size:14px;cursor:pointer;">💾 Save Image</button>
     </div>
   `;
   
   if (isCanvas) {
     layer.innerHTML = `
       <div style="position:relative;max-width:90%;max-height:70vh;border-radius:12px;overflow:hidden;box-shadow:0 25px 50px rgba(0,0,0,0.5);">
         <img src="${dataUrl}" style="max-width:100%;max-height:70vh;display:block;" alt="Watermarked">
       </div>
       ${textSection}
       <button id="wm-close-btn" style="position:fixed;top:20px;right:20px;padding:10px 20px;background:#ef4444;color:white;border:none;border-radius:8px;cursor:pointer;font-size:14px;z-index:1000000;">✕ Close</button>
     `;
     
     setTimeout(() => {
       const downloadBtn = document.getElementById('wm-download-btn');
       if (downloadBtn) {
         downloadBtn.onclick = () => {
           const link = document.createElement('a');
           link.download = 'watermarked-image.jpg';
           link.href = dataUrl;
           link.click();
         };
       }
     }, 100);
     
   } else {
     const cleanSrc = getHighResUrl(dataUrl);
     layer.innerHTML = `
       <div style="position:relative;display:inline-block;max-width:90%;max-height:70vh;border-radius:12px;overflow:hidden;box-shadow:0 25px 50px rgba(0,0,0,0.5);">
         <img src="${cleanSrc}" style="max-width:100%;max-height:70vh;display:block;" alt="Product">
         <div style="position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;overflow:hidden;">
           <div style="position:absolute;top:-50%;left:-50%;width:200%;height:200%;background:repeating-linear-gradient(45deg,transparent,transparent 80px,rgba(255,255,255,0.06) 80px,rgba(255,255,255,0.06) 160px);transform:rotate(15deg);"></div>
           <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-25deg);font-size:clamp(28px,5vw,72px);font-weight:900;color:rgba(255,255,255,0.28);white-space:nowrap;text-shadow:0 2px 20px rgba(0,0,0,0.4);letter-spacing:4px;">${encodedText}</div>
           <div style="position:absolute;width:100%;height:100%;display:flex;flex-wrap:wrap;align-content:space-around;justify-content:space-around;opacity:0.1;">
             ${Array(9).fill(`<span style="font-size:clamp(10px,1.5vw,16px);font-weight:700;color:#fff;transform:rotate(-30deg);padding:20px 40px;white-space:nowrap;">${encodedText}</span>`).join('')}
           </div>
         </div>
       </div>
       ${textSection}
       <button id="wm-close-btn" style="position:fixed;top:20px;right:20px;padding:10px 20px;background:#ef4444;color:white;border:none;border-radius:8px;cursor:pointer;font-size:14px;z-index:1000000;">✕ Close</button>
     `;
   }
   
   setTimeout(() => {
     const closeBtn = document.getElementById('wm-close-btn');
     if (closeBtn) closeBtn.onclick = () => layer.remove();
     
     const downloadBtn = document.getElementById('wm-download-btn');
     if (downloadBtn && !isCanvas) {
       downloadBtn.onclick = () => {
         alert('Please right-click the image and select "Save image as" to download.');
       };
     }
   }, 100);
   
   const escHandler = (e) => {
     if (e.key === 'Escape') {
       layer.remove();
       document.removeEventListener('keydown', escHandler);
     }
   };
   document.addEventListener('keydown', escHandler);
   
   document.body.appendChild(layer);
 }

 // ============ 主处理函数 ============

 async function handleImageDownload(imgSrc) {
   if (!imgSrc) return;
   log('Handling download for:', imgSrc);

   const toast = document.createElement('div');
   toast.innerHTML = '⏳ Generating watermarked image...';
   toast.style.cssText = `
     position: fixed;
     top: 50%;
     left: 50%;
     transform: translate(-50%, -50%);
     background: rgba(0,0,0,0.85);
     color: white;
     padding: 20px 32px;
     border-radius: 12px;
     z-index: 100000;
     font-family: Arial, sans-serif;
     font-size: 15px;
   `;
   document.body.appendChild(toast);

   try {
     const dataUrl = await generateCanvasWatermark(imgSrc);
     document.body.removeChild(toast);
     showPreview(dataUrl, true);
     log('Canvas watermark applied');
   } catch (err) {
     log('Canvas failed, using CSS overlay:', err.message);
     document.body.removeChild(toast);
     showPreview(imgSrc, false);
   }
 }

 // ============ 隐形水印层 ============

 function addInvisibleOverlay(container) {
   if (!CONFIG.invisibleWatermark) return;
   if (container.querySelector('.wm-invisible-overlay')) return;
   
   const overlay = document.createElement('div');
   overlay.className = 'wm-invisible-overlay';
   overlay.style.cssText = `
     position: absolute;
     top: 0; left: 0;
     width: 100%; height: 100%;
     pointer-events: none;
     z-index: 9999;
     background-image:
       repeating-linear-gradient(45deg, transparent, transparent 59px, rgba(200,200,200,${CONFIG.invisibleOpacity}) 59px, rgba(200,200,200,${CONFIG.invisibleOpacity}) 60px),
       repeating-linear-gradient(-45deg, transparent, transparent 59px, rgba(200,200,200,${CONFIG.invisibleOpacity}) 59px, rgba(200,200,200,${CONFIG.invisibleOpacity}) 60px);
     background-size: 120px 120px;
   `;
   
   const computed = window.getComputedStyle(container);
   if (computed.position === 'static') {
     container.style.position = 'relative';
   }
   container.appendChild(overlay);
 }

 // ============ 事件拦截 ============

 function interceptShadowDOM(shadowRoot, hostElement) {
   shadowRoot.addEventListener('contextmenu', (e) => {
     const img = e.target.closest('img');
     if (!img) return;
     e.preventDefault();
     e.stopPropagation();
     
     const largestUrl = getLargestImageFromSrcset(img);
     handleImageDownload(largestUrl);
   }, true);
   
   shadowRoot.addEventListener('dragstart', (e) => {
     if (e.target.tagName === 'IMG') e.preventDefault();
   }, true);
   
   if (hostElement) addInvisibleOverlay(hostElement);
 }

 function interceptContainer(container) {
   container.addEventListener('contextmenu', (e) => {
     const img = e.target.closest('img');
     if (!img) return;
     e.preventDefault();
     
     const largestUrl = getLargestImageFromSrcset(img);
     handleImageDownload(largestUrl);
   });
   
   container.addEventListener('dragstart', (e) => {
     if (e.target.tagName === 'IMG') e.preventDefault();
   });
   
   addInvisibleOverlay(container);
 }

 // ============ 扫描初始化 ============

 function scanAndProtect() {
   const shadowSelectors = [
     'product-slider', 'product-gallery', 'media-gallery', 'product-media',
     '[class*="product-gallery"]', '[class*="product-image"]'
   ];
   
   shadowSelectors.forEach(selector => {
     document.querySelectorAll(selector).forEach(el => {
       if (el.shadowRoot) interceptShadowDOM(el.shadowRoot, el);
     });
   });
   
   const containerSelectors = [
     '.product-image-container', '.product-gallery-container',
     '.product-gallery', '.product-images',
     '#Product-Slider', '#Product-Thumbnails',
     '[class*="gallery-content"]', '[class*="product-grid-container"]'
   ];
   
   containerSelectors.forEach(selector => {
     document.querySelectorAll(selector).forEach(el => interceptContainer(el));
   });
   
   document.addEventListener('contextmenu', (e) => {
     const img = e.target.closest('img');
     if (!img) return;
     const isProductImage = img.closest('[class*="product"], product-slider, product-thumbnails, [class*="gallery"]');
     if (isProductImage && !e.defaultPrevented) {
       e.preventDefault();
       
       const largestUrl = getLargestImageFromSrcset(img);
       handleImageDownload(largestUrl);
     }
   });
   
   document.addEventListener('dragstart', (e) => {
     const img = e.target.closest('img');
     if (img && img.closest('[class*="product"], product-slider, product-thumbnails')) {
       e.preventDefault();
     }
   });
 }

 function observeMutations() {
   const observer = new MutationObserver((mutations) => {
     let shouldRescan = false;
     mutations.forEach(mutation => {
       mutation.addedNodes.forEach(node => {
         if (node.nodeType !== 1) return;
         if (node.matches?.('product-slider, product-gallery, product-thumbnails') ||
             node.querySelector?.('product-slider, product-gallery, product-thumbnails') ||
             node.matches?.('[class*="product-image"], [class*="gallery"]')) {
           shouldRescan = true;
         }
       });
     });
     if (shouldRescan) setTimeout(scanAndProtect, 200);
   });
   observer.observe(document.body, { childList: true, subtree: true });
 }

 function init() {
   if (document.readyState === 'loading') {
     document.addEventListener('DOMContentLoaded', () => {
       scanAndProtect();
       observeMutations();
     });
   } else {
     scanAndProtect();
     observeMutations();
   }
   log('Watermark protection initialized');
 }

 init();

})();
